import { useState } from "react";
import { Search, Loader2, CheckCircle2, Clock, Star } from "lucide-react";

interface RespuestaItem {
  pregunta: string;
  tipo: "texto" | "seleccion";
  respuestaTexto?: string | null;
  respuestaOpcion?: string | null;
}
interface EnvioItem {
  envioId: number;
  titulo: string;
  estado: "pendiente" | "respondida" | "expirada";
  enviadoAt: string;
  respondidoAt?: string | null;
  respuestas: RespuestaItem[];
}

function fmtFecha(s: string) {
  return new Date(s).toLocaleDateString("es-VE", { day: "numeric", month: "short", year: "numeric" });
}

export default function MisEncuestasPage() {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [envios, setEnvios] = useState<EnvioItem[] | null>(null);
  const [abierto, setAbierto] = useState<number | null>(null);

  async function buscar(e?: React.FormEvent) {
    e?.preventDefault();
    setErr(null);
    setEnvios(null);
    if (q.trim().length < 3) { setErr("Ingresá tu cédula, email o teléfono (mínimo 3 caracteres)."); return; }
    setBusy(true);
    try {
      const r = await fetch("/api/v1/encuestas/mis-respuestas", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identificador: q.trim() }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message ?? "No pudimos buscar tus encuestas.");
      const data = await r.json();
      setEnvios(data.envios ?? []);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-ink-50 py-10 px-4">
      <div className="max-w-lg mx-auto space-y-5">
        <header className="text-center">
          <Star className="size-8 text-amber-500 mx-auto mb-2" />
          <h1 className="font-display font-bold text-xl text-ink-800">Mis encuestas</h1>
          <p className="text-sm text-ink-500 mt-1">Consultá el historial de encuestas que respondiste</p>
        </header>

        <form onSubmit={buscar} className="card p-4 flex gap-2">
          <input className="input flex-1" placeholder="Cédula, email o teléfono"
            value={q} onChange={(e) => setQ(e.target.value)} />
          <button type="submit" disabled={busy}
            className="px-4 rounded-lg bg-brand-600 hover:bg-brand-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Search className="size-4" />}
          </button>
        </form>

        {err && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">{err}</div>}

        {envios && envios.length === 0 && (
          <p className="text-center text-sm text-ink-400">No encontramos encuestas asociadas a ese dato.</p>
        )}

        {envios && envios.length > 0 && (
          <div className="space-y-2">
            {envios.map((e) => (
              <div key={e.envioId} className="card overflow-hidden">
                <button onClick={() => setAbierto(abierto === e.envioId ? null : e.envioId)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-ink-50/50">
                  <span className="flex-1 font-medium text-sm text-ink-800">{e.titulo}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${
                    e.estado === "respondida" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                  }`}>
                    {e.estado === "respondida" ? <CheckCircle2 className="size-3" /> : <Clock className="size-3" />}
                    {e.estado === "respondida" ? "Respondida" : "Pendiente"}
                  </span>
                  <span className="text-xs text-ink-400">{fmtFecha(e.respondidoAt ?? e.enviadoAt)}</span>
                </button>
                {abierto === e.envioId && e.estado === "respondida" && (
                  <div className="px-4 pb-3 space-y-2 border-t border-ink-100 pt-2">
                    {e.respuestas.map((r, i) => (
                      <div key={i}>
                        <p className="text-xs font-medium text-ink-600">{r.pregunta}</p>
                        <p className="text-sm text-ink-800">{r.respuestaOpcion ?? r.respuestaTexto ?? "—"}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
